import type { Locale } from '../index'

/**
 * Services page copy in all three locales (en / zh-Hant / zh-Hans).
 *
 * zh-Hant (Traditional — Taiwan / HK) and zh-Hans (Simplified — Mainland China)
 * are distinct, idiomatic translations — never auto-converted from one to the
 * other. Brand names, company name, emails/phones/URLs, anchor ids and pure
 * numbers are intentionally left untranslated.
 */
export const services: Record<Locale, Record<string, string>> = {
  en: {
    'services.head.title': 'Services — Geo4Logistics',
    'services.head.description':
      'Ocean, air, rail and road freight, customs brokerage and warehousing — planned, booked and tracked by one Geo4Logistics team.',

    'services.hero.title': 'Sea, air, rail and road freight',
    'services.hero.body':
      'Ocean and air freight are our core service, with rail and road where they fit. We plan the journey and handle the customs, documents and last mile along the way.',
    'services.hero.cta.quote': 'Get a quote',
    'services.hero.cta.browse': 'Browse services',

    'services.ocean.eyebrow': 'Ocean freight',
    'services.ocean.title': 'FCL & LCL, port-to-port or door-to-door',
    'services.ocean.body':
      'Our core service. We book space with major carriers, consolidate smaller volumes, and keep containers moving — with each milestone visible in your portal.',
    'services.ocean.feat.0': 'Full container load (FCL)',
    'services.ocean.feat.1': 'Less-than-container (LCL) consolidation',
    'services.ocean.feat.2': 'Reefer & out-of-gauge cargo',
    'services.ocean.feat.3': 'Port-to-port & door-to-door',
    'services.ocean.feat.4': 'Carrier-neutral routing',
    'services.ocean.feat.5': 'Container & vessel tracking',

    'services.air.eyebrow': 'Air freight',
    'services.air.title': 'Express and consolidated air freight',
    'services.air.body':
      'For time-critical, high-value or perishable cargo, we move it by air — express or consolidated — with the documentation and handling lined up so it clears and arrives on schedule.',
    'services.air.feat.0': 'Express & deferred service',
    'services.air.feat.1': 'Consolidated air consignments',
    'services.air.feat.2': 'Airport-to-airport & door',
    'services.air.feat.3': 'Time-critical & AOG',
    'services.air.feat.4': 'Temperature-sensitive cargo',
    'services.air.feat.5': 'AWB & live status',

    'services.rail.eyebrow': 'Rail freight',
    'services.rail.title': 'A middle path between ocean and air',
    'services.rail.body':
      'China–Europe block trains and intermodal rail sit between the two — faster than ocean, cheaper than air, and with a lower carbon footprint than long-haul road.',
    'services.rail.feat.0': 'China–Europe block trains',
    'services.rail.feat.1': 'FCL & LCL rail options',
    'services.rail.feat.2': 'Intermodal rail + road',
    'services.rail.feat.3': 'Lower cost than air freight',
    'services.rail.feat.4': 'Lower carbon than road',
    'services.rail.feat.5': 'End-to-end tracking',

    'services.road.eyebrow': 'Road & customs',
    'services.road.title': 'Trucking, last mile and customs',
    'services.road.body':
      "Cross-border trucking, last-mile delivery and customs brokerage to handle the ends of the journey. Our specialists manage classification and clearance so cargo doesn't sit.",
    'services.road.feat.0': 'Cross-border & domestic trucking',
    'services.road.feat.1': 'FTL & LTL',
    'services.road.feat.2': 'Customs brokerage & clearance',
    'services.road.feat.3': 'HS classification & duties',
    'services.road.feat.4': 'First & final-mile delivery',
    'services.road.feat.5': 'Proof of delivery',

    'services.valueadds.eyebrow': 'Beyond transport',
    'services.valueadds.title': 'Services around your freight',
    'services.valueadds.body':
      'Support services that wrap around your shipments, from warehousing to documentation and reporting.',
    'services.valueadds.0.title': 'Warehousing & distribution',
    'services.valueadds.0.body':
      'Storage, pick-and-pack and onward distribution near key ports and hubs.',
    'services.valueadds.1.title': 'Cargo insurance',
    'services.valueadds.1.body':
      "All-risk coverage arranged per shipment, so a single incident doesn't become your problem.",
    'services.valueadds.2.title': 'Project & special cargo',
    'services.valueadds.2.body':
      'Oversized, heavy-lift and multi-leg project moves coordinated end to end.',
    'services.valueadds.3.title': 'Documentation',
    'services.valueadds.3.body':
      'B/L, certificates of origin, packing lists and arrival notices — prepared and stored in your portal.',
    'services.valueadds.4.title': 'Reporting & analytics',
    'services.valueadds.4.body':
      'Volume, spend and on-time performance by lane, so you can plan the next quarter with data.',
    'services.valueadds.5.title': 'API & integrations',
    'services.valueadds.5.body':
      'Push bookings and pull status into your own systems with our developer API and webhooks.',

    'services.compare.eyebrow': 'Choosing a mode',
    'services.compare.title': 'Which service fits your shipment?',
    'services.compare.body':
      'A quick starting point — our coordinators help you weigh cost, speed and cargo type for each lane.',
    'services.compare.col.mode': 'Mode',
    'services.compare.col.bestfor': 'Best for',
    'services.compare.col.speed': 'Relative speed',
    'services.compare.col.cost': 'Relative cost',
    'services.compare.col.door': 'Door-to-door',
    'services.compare.yes': 'Yes',
    'services.compare.ocean.mode': 'Ocean',
    'services.compare.ocean.bestfor': 'High volume, non-urgent',
    'services.compare.ocean.speed': 'Slowest',
    'services.compare.ocean.cost': 'Lowest',
    'services.compare.rail.mode': 'Rail',
    'services.compare.rail.bestfor': 'Asia–Europe, balanced',
    'services.compare.rail.speed': 'Medium',
    'services.compare.rail.cost': 'Medium',
    'services.compare.air.mode': 'Air',
    'services.compare.air.bestfor': 'Urgent, high-value',
    'services.compare.air.speed': 'Fastest',
    'services.compare.air.cost': 'Highest',
    'services.compare.road.mode': 'Road',
    'services.compare.road.bestfor': 'Regional & first/final mile',
    'services.compare.road.speed': 'Fast (regional)',
    'services.compare.road.cost': 'Medium',

    'services.cta.title': 'Not sure which mode you need?',
    'services.cta.body':
      "Tell us your cargo, lane and deadline. We'll recommend the route and send itemised pricing — usually within 1–2 business days.",
    'services.cta.quote': 'Get a quote',
    'services.cta.help': 'Visit the help centre',
  },

  'zh-Hant': {
    'services.head.title': '服務 — Geo4Logistics',
    'services.head.description':
      '海運、空運、鐵路與陸運貨運，加上報關與倉儲 — 由 Geo4Logistics 同一團隊規劃、訂艙並追蹤。',

    'services.hero.title': '海運、空運、鐵路與陸運',
    'services.hero.body':
      '海運與空運是我們的核心服務，並於合適時提供鐵路與陸運。我們規劃整段運程，並一路處理報關、文件與末端配送。',
    'services.hero.cta.quote': '取得報價',
    'services.hero.cta.browse': '瀏覽服務',

    'services.ocean.eyebrow': '海運',
    'services.ocean.title': '整櫃與拼櫃，港到港或門到門',
    'services.ocean.body':
      '我們的核心服務。我們向主要船公司訂艙、為較小貨量提供併櫃，並讓貨櫃持續流轉 — 每個節點都可在您的客戶平台查看。',
    'services.ocean.feat.0': '整櫃（FCL）',
    'services.ocean.feat.1': '拼櫃（LCL）併櫃',
    'services.ocean.feat.2': '冷凍櫃與超尺寸貨物',
    'services.ocean.feat.3': '港到港與門到門',
    'services.ocean.feat.4': '不限船公司的路線安排',
    'services.ocean.feat.5': '貨櫃與船舶追蹤',

    'services.air.eyebrow': '空運',
    'services.air.title': '快遞與併裝空運',
    'services.air.body':
      '對於時效要求高、高價值或易腐的貨物，我們以空運運送 — 快遞或併裝 — 並備妥文件與處理流程，確保順利清關並準時抵達。',
    'services.air.feat.0': '快遞與緩遞服務',
    'services.air.feat.1': '空運併裝貨件',
    'services.air.feat.2': '機場到機場與門到門',
    'services.air.feat.3': '時效急件與飛機停場待料（AOG）',
    'services.air.feat.4': '溫控貨物',
    'services.air.feat.5': '空運提單（AWB）與即時狀態',

    'services.rail.eyebrow': '鐵路運輸',
    'services.rail.title': '介於海運與空運之間的選擇',
    'services.rail.body':
      '中歐班列與多式聯運鐵路介於兩者之間 — 比海運快、比空運便宜，且碳足跡低於長途陸運。',
    'services.rail.feat.0': '中歐班列',
    'services.rail.feat.1': '整櫃與拼櫃鐵路選項',
    'services.rail.feat.2': '鐵路加陸運多式聯運',
    'services.rail.feat.3': '成本低於空運',
    'services.rail.feat.4': '碳排放低於陸運',
    'services.rail.feat.5': '全程追蹤',

    'services.road.eyebrow': '陸運與報關',
    'services.road.title': '貨車運輸、末端配送與報關',
    'services.road.body':
      '跨境貨車運輸、末端配送與報關代理，處理運程的兩端。我們的專員負責商品分類與清關，讓貨物不致滯留。',
    'services.road.feat.0': '跨境與境內貨車運輸',
    'services.road.feat.1': '整車（FTL）與零擔（LTL）',
    'services.road.feat.2': '報關代理與清關',
    'services.road.feat.3': 'HS 稅則分類與關稅',
    'services.road.feat.4': '首端與末端配送',
    'services.road.feat.5': '簽收證明',

    'services.valueadds.eyebrow': '運輸之外',
    'services.valueadds.title': '圍繞貨運的周邊服務',
    'services.valueadds.body': '環繞您貨件的支援服務，從倉儲到文件與報表一應俱全。',
    'services.valueadds.0.title': '倉儲與配送',
    'services.valueadds.0.body': '於主要港口與樞紐附近提供倉儲、揀貨包裝及後續配送。',
    'services.valueadds.1.title': '貨物保險',
    'services.valueadds.1.body': '依每筆貨件安排全險保障，讓單一事故不會成為您的負擔。',
    'services.valueadds.2.title': '工程與特殊貨物',
    'services.valueadds.2.body': '超大件、重吊與多段式工程貨物，全程統籌協調。',
    'services.valueadds.3.title': '文件作業',
    'services.valueadds.3.body': '提單、原產地證明、裝箱單與到貨通知 — 備妥並儲存於您的客戶平台。',
    'services.valueadds.4.title': '報表與分析',
    'services.valueadds.4.body': '依航線提供貨量、支出與準時表現，讓您能憑數據規劃下一季。',
    'services.valueadds.5.title': 'API 與系統整合',
    'services.valueadds.5.body':
      '透過我們的開發者 API 與 webhook，將訂艙推送至您自有系統並回拉狀態。',

    'services.compare.eyebrow': '選擇運送方式',
    'services.compare.title': '哪一種服務最適合您的貨件？',
    'services.compare.body':
      '提供一個快速的起點 — 我們的協調專員會協助您針對各航線權衡成本、速度與貨物類型。',
    'services.compare.col.mode': '運送方式',
    'services.compare.col.bestfor': '最適合',
    'services.compare.col.speed': '相對速度',
    'services.compare.col.cost': '相對成本',
    'services.compare.col.door': '門到門',
    'services.compare.yes': '可',
    'services.compare.ocean.mode': '海運',
    'services.compare.ocean.bestfor': '大批量、非急件',
    'services.compare.ocean.speed': '最慢',
    'services.compare.ocean.cost': '最低',
    'services.compare.rail.mode': '鐵路',
    'services.compare.rail.bestfor': '亞歐之間、均衡考量',
    'services.compare.rail.speed': '中等',
    'services.compare.rail.cost': '中等',
    'services.compare.air.mode': '空運',
    'services.compare.air.bestfor': '急件、高價值',
    'services.compare.air.speed': '最快',
    'services.compare.air.cost': '最高',
    'services.compare.road.mode': '陸運',
    'services.compare.road.bestfor': '區域與首末端配送',
    'services.compare.road.speed': '快速（區域）',
    'services.compare.road.cost': '中等',

    'services.cta.title': '不確定該用哪一種運送方式？',
    'services.cta.body':
      '請告訴我們您的貨物、航線與期限。我們會推薦路線並提供逐項報價 — 通常於一至兩個工作天內完成。',
    'services.cta.quote': '取得報價',
    'services.cta.help': '前往支援中心',
  },

  'zh-Hans': {
    'services.head.title': '服务 — Geo4Logistics',
    'services.head.description':
      '海运、空运、铁路与公路运输，以及报关与仓储 — 由 Geo4Logistics 同一团队规划、订舱并追踪。',

    'services.hero.title': '海运、空运、铁路与公路运输',
    'services.hero.body':
      '海运与空运是我们的核心服务，并在合适时提供铁路与公路运输。我们规划整段运程，并一路处理报关、单证与末端配送。',
    'services.hero.cta.quote': '获取报价',
    'services.hero.cta.browse': '浏览服务',

    'services.ocean.eyebrow': '海运',
    'services.ocean.title': '整箱与拼箱，港到港或门到门',
    'services.ocean.body':
      '我们的核心服务。我们向主要船公司订舱、为较小货量提供拼箱，并让集装箱持续流转 — 每个节点都可在您的客户平台查看。',
    'services.ocean.feat.0': '整箱（FCL）',
    'services.ocean.feat.1': '拼箱（LCL）拼货',
    'services.ocean.feat.2': '冷藏箱与超尺寸货物',
    'services.ocean.feat.3': '港到港与门到门',
    'services.ocean.feat.4': '不限船公司的路线安排',
    'services.ocean.feat.5': '集装箱与船舶追踪',

    'services.air.eyebrow': '空运',
    'services.air.title': '快递与拼装空运',
    'services.air.body':
      '对于时效要求高、高价值或易腐的货物，我们以空运运送 — 快递或拼装 — 并备妥单证与操作流程，确保顺利清关并准时抵达。',
    'services.air.feat.0': '快递与缓运服务',
    'services.air.feat.1': '空运拼装货件',
    'services.air.feat.2': '机场到机场与门到门',
    'services.air.feat.3': '时效急件与飞机停场待料（AOG）',
    'services.air.feat.4': '温控货物',
    'services.air.feat.5': '空运提单（AWB）与实时状态',

    'services.rail.eyebrow': '铁路运输',
    'services.rail.title': '介于海运与空运之间的选择',
    'services.rail.body':
      '中欧班列与多式联运铁路介于两者之间 — 比海运快、比空运便宜，且碳足迹低于长途公路运输。',
    'services.rail.feat.0': '中欧班列',
    'services.rail.feat.1': '整箱与拼箱铁路选项',
    'services.rail.feat.2': '铁路加公路多式联运',
    'services.rail.feat.3': '成本低于空运',
    'services.rail.feat.4': '碳排放低于公路运输',
    'services.rail.feat.5': '全程追踪',

    'services.road.eyebrow': '公路与报关',
    'services.road.title': '卡车运输、末端配送与报关',
    'services.road.body':
      '跨境卡车运输、末端配送与报关代理，处理运程的两端。我们的专员负责商品归类与清关，让货物不致滞留。',
    'services.road.feat.0': '跨境与境内卡车运输',
    'services.road.feat.1': '整车（FTL）与零担（LTL）',
    'services.road.feat.2': '报关代理与清关',
    'services.road.feat.3': 'HS 编码归类与关税',
    'services.road.feat.4': '首端与末端配送',
    'services.road.feat.5': '签收证明',

    'services.valueadds.eyebrow': '运输之外',
    'services.valueadds.title': '围绕货运的周边服务',
    'services.valueadds.body': '环绕您货件的支持服务，从仓储到单证与报表一应俱全。',
    'services.valueadds.0.title': '仓储与配送',
    'services.valueadds.0.body': '在主要港口与枢纽附近提供仓储、拣货包装及后续配送。',
    'services.valueadds.1.title': '货物保险',
    'services.valueadds.1.body': '按每笔货件安排一切险保障，让单一事故不会成为您的负担。',
    'services.valueadds.2.title': '工程与特种货物',
    'services.valueadds.2.body': '超大件、重吊与多段式工程货物，全程统筹协调。',
    'services.valueadds.3.title': '单证作业',
    'services.valueadds.3.body': '提单、原产地证书、装箱单与到货通知 — 备妥并存储于您的客户平台。',
    'services.valueadds.4.title': '报表与分析',
    'services.valueadds.4.body': '按航线提供货量、支出与准时表现，让您能凭数据规划下一季度。',
    'services.valueadds.5.title': 'API 与系统集成',
    'services.valueadds.5.body':
      '通过我们的开发者 API 与 webhook，将订舱推送至您自有系统并回拉状态。',

    'services.compare.eyebrow': '选择运输方式',
    'services.compare.title': '哪种服务最适合您的货件？',
    'services.compare.body':
      '提供一个快速的起点 — 我们的协调专员会帮助您针对各航线权衡成本、速度与货物类型。',
    'services.compare.col.mode': '运输方式',
    'services.compare.col.bestfor': '最适合',
    'services.compare.col.speed': '相对速度',
    'services.compare.col.cost': '相对成本',
    'services.compare.col.door': '门到门',
    'services.compare.yes': '可',
    'services.compare.ocean.mode': '海运',
    'services.compare.ocean.bestfor': '大批量、非急件',
    'services.compare.ocean.speed': '最慢',
    'services.compare.ocean.cost': '最低',
    'services.compare.rail.mode': '铁路',
    'services.compare.rail.bestfor': '亚欧之间、均衡考量',
    'services.compare.rail.speed': '中等',
    'services.compare.rail.cost': '中等',
    'services.compare.air.mode': '空运',
    'services.compare.air.bestfor': '急件、高价值',
    'services.compare.air.speed': '最快',
    'services.compare.air.cost': '最高',
    'services.compare.road.mode': '公路',
    'services.compare.road.bestfor': '区域与首末端配送',
    'services.compare.road.speed': '快速（区域）',
    'services.compare.road.cost': '中等',

    'services.cta.title': '不确定该用哪种运输方式？',
    'services.cta.body':
      '请告诉我们您的货物、航线与期限。我们会推荐路线并提供逐项报价 — 通常在一到两个工作日内完成。',
    'services.cta.quote': '获取报价',
    'services.cta.help': '前往帮助中心',
  },
}
